import React, { useState, useEffect, useRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { isSyncedTask, type Priority, type Task, type CreateTaskInput, type UpdateTaskInput } from "@/hooks/useTasks";
import ImportedTaskActionModal from "@/components/mirror/ImportedTaskActionModal";
import { useLibraryIdentity } from "@/hooks/useLibraryIdentity";
import { snapshotForTask } from "@/lib/taskMutations";
import { newTaskOperationId, type TaskSnapshot } from "@/lib/taskExecutor";

interface TaskModalProps {
  open: boolean;
  initialTitle?: string;
  task?: Task | null;
  onClose: () => void;
  onCreate?: (input: CreateTaskInput) => Promise<unknown>;
  onUpdate?: (id: string, fields: UpdateTaskInput, expectedSnapshot?: TaskSnapshot) => Promise<void>;
  onRefreshReview?: () => void;
}

const PRIORITIES: Priority[] = ["Normal", "Important", "Critical"];
const priorityKeys: Record<Priority, string> = {
  Critical: "tasks.priority.critical", Important: "tasks.priority.important", Normal: "tasks.priority.normal",
};
const fieldClass = "w-full min-h-12 rounded-xl border border-primary/20 bg-background/60 px-3 py-3 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50";

const TaskModal: React.FC<TaskModalProps> = props => {
  if (props.task && isSyncedTask(props.task)) return <ImportedTaskActionModal open={props.open} task={props.task} action="update" onClose={props.onClose} />;
  return props.open ? <BoundTaskForm {...props} /> : null;
};
function BoundTaskForm(props: TaskModalProps) {
  const identity = useLibraryIdentity();
  if (!identity.ready || !identity.userId || props.task && props.task.user_id !== identity.userId) return null;
  return <LocalTaskForm key={identity.userId + ":" + identity.generation + ":" + (props.task?.id ?? "create")} {...props} assertCurrent={identity.assertCurrent} />;
}
const LocalTaskForm: React.FC<TaskModalProps & { assertCurrent: () => Promise<void> }> = ({ open, initialTitle = "", task = null, onClose, onCreate, onUpdate, onRefreshReview, assertCurrent }) => {
  const { t } = useLanguage();
  const isEdit = task !== null;
  const [title, setTitle] = useState(initialTitle);
  const [priority, setPriority] = useState<Priority>("Normal");
  const [dueDate, setDueDate] = useState("");
  const [timeLabel, setTimeLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [stale, setStale] = useState(false);
  const [draftId] = useState(newTaskOperationId);
  const [originalTask] = useState(() => task ? structuredClone(task) : null);
  const [reviewedSnapshot] = useState(() => task ? snapshotForTask(task) : null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; const lifecycle = mounted; return () => { lifecycle.current = false; }; }, []);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setTitle(task?.title ?? initialTitle);
    setPriority(task?.priority ?? "Normal");
    setDueDate(task?.due_date?.slice(0, 10) ?? "");
    setTimeLabel(task?.time_label ?? "");
    setSaveError(false);
  // A resource refresh for the same ID cannot silently replace this review.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const requestClose = () => { if (!savingRef.current) onClose(); };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim() || savingRef.current || stale || task && !reviewedSnapshot) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    try {
      await assertCurrent(); if (!mounted.current) return;
      const fields = {
        title: title.trim(), priority,
        // Preserve the existing date representation; timezone behavior is a separate change.
        due_date: dueDate === originalTask?.due_date?.slice(0, 10) ? originalTask.due_date : dueDate || null,
        time_label: timeLabel || null,
      };
      if (task && onUpdate) await onUpdate(task.id, fields, reviewedSnapshot!);
      else if (!task && onCreate) {
        const created = await onCreate({ ...fields, id: draftId });
        if (created == null) throw new Error("Task creation was not confirmed");
      } else throw new Error("Task save handler unavailable");
      await assertCurrent(); if (mounted.current) onClose();
    } catch (error) {
      console.error("[TaskModal] Save failed", error);
      if (mounted.current) { setSaveError(true); if (error instanceof Error && /changed|stale|snapshot/.test(error.message)) setStale(true); }
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  if (task && isSyncedTask(task)) return <ImportedTaskActionModal
    open={open} task={task} action="update" onClose={onClose} />;

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => { if (!nextOpen) requestClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
        <Dialog.Content
          onEscapeKeyDown={(e) => { if (savingRef.current) e.preventDefault(); }}
          onInteractOutside={(e) => { if (savingRef.current) e.preventDefault(); }}
          className="fixed bottom-0 left-1/2 z-50 w-full max-w-lg -translate-x-1/2 max-h-[90dvh] overflow-y-auto rounded-t-3xl border border-primary/20 bg-[#10172a] p-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-2xl sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:rounded-3xl">
          <div className="flex items-start justify-between gap-3 mb-5">
            <div>
              <Dialog.Title className="font-display text-xl font-semibold text-foreground">
                {t(isEdit ? "tasks.modal.edit_title" : "tasks.modal.new_title")}
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-sm text-muted-foreground">
                {t("ux.tasks.formHint")}
              </Dialog.Description>
            </div>
            <button type="button" onClick={requestClose} disabled={saving} aria-label={t("common.close")}
              className="min-w-11 min-h-11 rounded-xl bg-background/50 flex items-center justify-center text-foreground disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-primary">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          <form onSubmit={handleSubmit} aria-busy={saving} className="space-y-5">
            <div>
              <label htmlFor="task-title" className="block text-sm text-foreground mb-2">{t("tasks.modal.label_title")}</label>
              <textarea rows={3} id="task-title" value={title} onChange={(e) => setTitle(e.target.value)}
                required disabled={saving || stale || isEdit && !reviewedSnapshot} maxLength={1000} autoFocus placeholder={t("tasks.capture_placeholder")} className={fieldClass} />
            </div>
            <fieldset disabled={saving || stale || isEdit && !reviewedSnapshot}>
              <legend className="text-sm text-foreground mb-2">{t("tasks.modal.label_priority")}</legend>
              <div className="flex flex-wrap gap-2">
                {PRIORITIES.map((p) => (
                  <label key={p} className={"flex-1 min-w-24 min-h-12 rounded-xl border px-3 py-3 flex items-center gap-2 text-sm cursor-pointer " +
                    (priority === p ? "border-primary/60 bg-primary/15 text-foreground" : "border-border bg-background/40 text-muted-foreground")}>
                    <input type="radio" name="task-priority" value={p} checked={priority === p}
                      onChange={() => setPriority(p)} className="accent-cyan-400 w-4 h-4 shrink-0" />
                    {t(priorityKeys[p])}
                  </label>
                ))}
              </div>
            </fieldset>
            <details key={open ? (task?.id ?? "new") : "closed"} open={isEdit || undefined}
              className="rounded-xl border border-border bg-background/30">
              <summary className="min-h-12 px-3 py-3 text-sm text-foreground cursor-pointer focus-visible:ring-2 focus-visible:ring-primary">
                {t("ux.tasks.schedule")} <ChevronDown size={14} className="inline ml-1" aria-hidden="true" />
              </summary>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 pt-0">
                <div>
                  <label htmlFor="task-date" className="block text-sm text-muted-foreground mb-2">{t("tasks.modal.label_date")}</label>
                  <input id="task-date" type="date" value={dueDate} disabled={saving || stale || isEdit && !reviewedSnapshot} onChange={(e) => setDueDate(e.target.value)}
                    className={fieldClass} style={{ colorScheme: "dark" }} />
                </div>
                <div>
                  <label htmlFor="task-time" className="block text-sm text-muted-foreground mb-2">{t("tasks.modal.label_time")}</label>
                  <input id="task-time" value={timeLabel} disabled={saving || stale || isEdit && !reviewedSnapshot} onChange={(e) => setTimeLabel(e.target.value)}
                    placeholder="09:00" maxLength={100} className={fieldClass} />
                </div>
              </div>
            </details>
            {(saveError || isEdit && !reviewedSnapshot) && <p role="alert" className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 text-sm text-foreground">{t(stale ? "taskAction.changed" : isEdit && !reviewedSnapshot ? "taskAction.readOnly" : "ux.tasks.saveError")}</p>}
            {stale && <button type="button" className={fieldClass} onClick={() => { onClose(); onRefreshReview?.(); }}>{t("taskAction.refreshReview")}</button>}
            {saving && <p role="status" className="text-sm text-muted-foreground">{t("ux.tasks.saving")}</p>}
            <div className="flex gap-3">
              <button type="button" onClick={requestClose} disabled={saving}
                className="flex-1 min-h-12 rounded-xl border border-border text-sm text-foreground disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-primary">
                {t("tasks.modal.cancel")}
              </button>
              <button type="submit" disabled={saving || stale || isEdit && !reviewedSnapshot || !title.trim()}
                className="flex-1 min-h-12 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-primary">
                {t(isEdit ? "ux.tasks.saveChanges" : "ux.tasks.create")}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
export default TaskModal;
